import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveSustainableFarming } from '../siteSustainableFarming';
import { fetchSite, getToken, patchSustainableFarming, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

const CARD_TITLES = ['Card 1 (left)', 'Card 2 (middle, staggered)', 'Card 3 (right)'];

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function ModalFooter({ onCancel, onApply }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={primaryBtnClass}>
        Apply
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

function sectionDraftFromForm(form) {
  return {
    sectionLabel: form.sectionLabel || '',
    sectionTitle: form.sectionTitle || '',
    intro: form.intro || '',
    ctaLabel: form.ctaLabel || '',
    ctaHref: form.ctaHref || '',
  };
}

function cardDraftFromCard(c) {
  return {
    image: c?.image ?? '',
    alt: c?.alt ?? '',
    stat: c?.stat ?? '',
    label: c?.label ?? '',
    offset: Boolean(c?.offset),
  };
}

export default function SustainableFarmingDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => resolveSustainableFarming(DEFAULT_SITE));
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingCard, setUploadingCard] = useState(false);

  const [modalErr, setModalErr] = useState('');
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [sectionDraft, setSectionDraft] = useState(null);
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [cardModalIndex, setCardModalIndex] = useState(null);
  const [cardDraft, setCardDraft] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((site) => setForm(resolveSustainableFarming(site)))
      .catch(() => setForm(resolveSustainableFarming(DEFAULT_SITE)));
  }, [navigate]);

  function closeSectionModal() {
    setSectionModalOpen(false);
    setSectionDraft(null);
    setModalErr('');
  }

  function openSectionModal() {
    setModalErr('');
    setSectionDraft(sectionDraftFromForm(form));
    setSectionModalOpen(true);
  }

  function applySectionModal() {
    if (!sectionDraft) return;
    setForm((f) => ({
      ...f,
      sectionLabel: sectionDraft.sectionLabel,
      sectionTitle: sectionDraft.sectionTitle,
      intro: sectionDraft.intro,
      ctaLabel: sectionDraft.ctaLabel,
      ctaHref: sectionDraft.ctaHref,
    }));
    closeSectionModal();
  }

  function closeCardModal() {
    setCardModalOpen(false);
    setCardModalIndex(null);
    setCardDraft(null);
    setModalErr('');
  }

  function openCardModal(index) {
    const c = form.cards?.[index];
    if (!c) return;
    setModalErr('');
    setCardDraft(cardDraftFromCard(c));
    setCardModalIndex(index);
    setCardModalOpen(true);
  }

  function applyCardModal() {
    if (cardDraft == null || cardModalIndex == null) return;
    setForm((f) => {
      const cards = [...(f.cards || [])];
      cards[cardModalIndex] = {
        ...cards[cardModalIndex],
        ...cardDraft,
        offset: Boolean(cardDraft.offset),
      };
      return { ...f, cards };
    });
    closeCardModal();
  }

  async function onCardDraftUpload(file) {
    if (!file) return;
    setErr('');
    setMsg('');
    setUploadingCard(true);
    try {
      const { url } = await uploadFile(file);
      setModalErr('');
      setCardDraft((d) => (d ? { ...d, image: url } : d));
      setMsg('Image uploaded to Cloudinary. Click Save to publish.');
    } catch (e) {
      const m = e.message || 'Upload failed';
      setErr(m);
      setModalErr(m);
    } finally {
      setUploadingCard(false);
    }
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const payload = {
        sectionLabel: form.sectionLabel,
        sectionTitle: form.sectionTitle,
        intro: form.intro,
        ctaLabel: form.ctaLabel,
        ctaHref: form.ctaHref,
        cards: form.cards.map((c) => ({
          image: c.image,
          alt: c.alt,
          stat: c.stat,
          label: c.label,
          offset: Boolean(c.offset),
        })),
      };
      const { sustainableFarming } = await patchSustainableFarming(payload);
      setForm(resolveSustainableFarming({ sustainableFarming: sustainableFarming || payload }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const cards = form.cards || [];

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">Sustainable farming</h1>
          <p className="text-sm text-neutral-600">Section copy, CTA, and three stat cards — edit in dialogs, then save</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="space-y-6">
          <section className={cardClass}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="m-0 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  {clip(form.sectionLabel, 36)}
                </p>
                <h2 className="mt-1 text-lg font-bold text-neutral-900">{clip(form.sectionTitle, 56)}</h2>
                <p className="mt-2 text-sm text-neutral-700">{clip(form.intro, 160)}</p>
                <p className="mt-2 text-xs text-neutral-500">
                  CTA: {clip(form.ctaLabel, 28)} → {clip(form.ctaHref, 40)}
                </p>
              </div>
              <button type="button" onClick={openSectionModal} className={secondaryBtnClass}>
                Edit in dialog
              </button>
            </div>
          </section>

          <section className={cardClass}>
            <h2 className="m-0 text-base font-bold text-neutral-900">Stat cards</h2>
            <p className="mt-1 text-xs text-neutral-500">Three image cards in the section layout</p>
            <ul className="mt-4 list-none space-y-2 p-0">
              {CARD_TITLES.map((title, i) => {
                const c = cards[i] || {};
                return (
                  <li
                    key={title}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2"
                  >
                    <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-neutral-200">
                      {c.image ? (
                        <img src={c.image} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[10px] text-neutral-400">No img</div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-xs font-semibold text-neutral-500">{title}</p>
                      <p className="mt-0.5 text-sm font-semibold text-neutral-900">
                        {clip(c.stat, 12)} <span className="font-normal text-neutral-600">{clip(c.label, 48)}</span>
                      </p>
                      {c.offset ? (
                        <p className="mt-0.5 text-[11px] text-brand-forest">Staggered on desktop</p>
                      ) : null}
                    </div>
                    <button type="button" className={ghostBtnClass} onClick={() => openCardModal(i)}>
                      Edit
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </div>

      <AdminModal
        open={sectionModalOpen}
        title="Section & CTA"
        description="Eyebrow, headline, intro, and primary button for the sustainable farming block."
        error={modalErr}
        onClose={closeSectionModal}
        footer={<ModalFooter onCancel={closeSectionModal} onApply={applySectionModal} />}
      >
        {sectionDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Eyebrow label
              <input
                className={input}
                value={sectionDraft.sectionLabel}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, sectionLabel: e.target.value }));
                }}
              />
            </label>
            <label className={label}>
              Headline
              <input
                className={input}
                value={sectionDraft.sectionTitle}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, sectionTitle: e.target.value }));
                }}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Intro paragraph
              <textarea
                className={`${input} min-h-[8rem]`}
                value={sectionDraft.intro}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, intro: e.target.value }));
                }}
              />
            </label>
            <label className={label}>
              CTA button label
              <input
                className={input}
                value={sectionDraft.ctaLabel}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, ctaLabel: e.target.value }));
                }}
              />
            </label>
            <label className={label}>
              CTA link (href)
              <input
                className={input}
                value={sectionDraft.ctaHref}
                onChange={(e) => {
                  setModalErr('');
                  setSectionDraft((d) => ({ ...d, ctaHref: e.target.value }));
                }}
              />
            </label>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={cardModalOpen}
        title={cardModalIndex != null ? `Edit ${CARD_TITLES[cardModalIndex]}` : 'Edit card'}
        description="Image, stat line, caption, and optional vertical stagger on desktop."
        error={modalErr}
        onClose={closeCardModal}
        footer={<ModalFooter onCancel={closeCardModal} onApply={applyCardModal} />}
      >
        {cardDraft ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            <div className="min-w-0 space-y-3">
              <label className={label}>
                Image URL
                <input
                  className={`${input} font-mono text-sm`}
                  value={cardDraft.image}
                  onChange={(e) => {
                    setModalErr('');
                    setCardDraft((d) => ({ ...d, image: e.target.value }));
                  }}
                  placeholder="https://…"
                />
              </label>
              <label className={`${label} mb-0`}>
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploadingCard}
                  className={fileInput}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    onCardDraftUpload(f);
                  }}
                />
              </label>
              {uploadingCard ? <p className="text-xs text-neutral-500">Uploading…</p> : null}
              <AdminImagePreview src={cardDraft.image} alt={cardDraft.alt || ''} />
              {cardDraft.image ? (
                <button
                  type="button"
                  onClick={() => setCardDraft((d) => (d ? { ...d, image: '' } : d))}
                  className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear image URL
                </button>
              ) : null}
            </div>
            <div className="min-w-0 space-y-3">
              <label className={label}>
                Image alt text
                <input
                  className={input}
                  value={cardDraft.alt}
                  onChange={(e) => setCardDraft((d) => ({ ...d, alt: e.target.value }))}
                />
              </label>
              <label className={label}>
                Stat (e.g. 80%)
                <input
                  className={input}
                  value={cardDraft.stat}
                  onChange={(e) => setCardDraft((d) => ({ ...d, stat: e.target.value }))}
                />
              </label>
              <label className={label}>
                Label (caption)
                <input
                  className={input}
                  value={cardDraft.label}
                  onChange={(e) => setCardDraft((d) => ({ ...d, label: e.target.value }))}
                />
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-800">
                <input
                  type="checkbox"
                  checked={Boolean(cardDraft.offset)}
                  onChange={(e) => setCardDraft((d) => (d ? { ...d, offset: e.target.checked } : d))}
                  className="h-4 w-4 rounded border-neutral-300 text-brand-olive focus:ring-brand-olive"
                />
                Stagger card down on desktop (middle card style)
              </label>
            </div>
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
